/**
 * Route Trace quests: one drawn route, auto Start/Finish pins on it, and a few
 * play settings. Shared by the worker (validation, snapping) and the quest editor.
 */
import { closestPointOnSegment, haversineM, lineDistanceM } from "./geo.ts";
import type { LngLat } from "./types.ts";

export const QUEST_TYPES = ["route_trace", "spot_trace"] as const;
export type QuestType = (typeof QUEST_TYPES)[number];

/** Walking pace for estimated quest time (4 km/h). */
export const WALK_MIN_PER_KM = 15;
/** A kept pin farther than this from a new route goes back to the route's end. */
export const PIN_RESET_M = 500;
export const MAX_ROUTE_POINTS = 5000;
export const MIN_TIME_LIMIT_MIN = 1;
export const MAX_TIME_LIMIT_MIN = 24 * 60;

export interface LineSnap {
  point: LngLat;
  /** Meters along the line from its first vertex. */
  alongM: number;
  distanceM: number;
}

export function snapToLine(p: LngLat, line: LngLat[]): LineSnap | null {
  if (line.length === 0) return null;
  if (line.length === 1) return { point: line[0]!, alongM: 0, distanceM: haversineM(p, line[0]!) };
  let best: LineSnap | null = null;
  let walked = 0;
  for (let i = 1; i < line.length; i++) {
    const a = line[i - 1]!;
    const b = line[i]!;
    const seg = haversineM(a, b);
    const hit = closestPointOnSegment(p, a, b);
    if (!best || hit.distanceM < best.distanceM) {
      best = { point: hit.point, alongM: walked + hit.t * seg, distanceM: hit.distanceM };
    }
    walked += seg;
  }
  return best;
}

export function walkMinutes(meters: number): number {
  if (!(meters > 0)) return 0;
  return Math.max(1, Math.round((meters / 1000) * WALK_MIN_PER_KM));
}

/** Snap a pin onto the route, or put it on the route's end when it is missing or far away. */
export function placePin(pin: LngLat | null, line: LngLat[], end: "start" | "finish"): LngLat {
  const fallback = end === "start" ? line[0]! : line[line.length - 1]!;
  if (!pin) return fallback;
  const snap = snapToLine(pin, line);
  if (!snap || snap.distanceM > PIN_RESET_M) return fallback;
  return snap.point;
}

/**
 * The line a fixed-start attempt is graded against: it begins at the creator's Start.
 * A loop is rotated to the Start; an open line is reversed when Start sits nearer its end.
 */
export function orientLineForStart(line: LngLat[], closed: boolean, start: LngLat | null): LngLat[] {
  if (!start || line.length < 2) return line;
  if (closed) {
    const ring = haversineM(line[0]!, line[line.length - 1]!) <= 1 ? line.slice(0, -1) : line.slice();
    let bestI = 0;
    let bestD = Infinity;
    ring.forEach((p, i) => {
      const d = haversineM(p, start);
      if (d < bestD) {
        bestD = d;
        bestI = i;
      }
    });
    const rotated = [...ring.slice(bestI), ...ring.slice(0, bestI)];
    return [...rotated, rotated[0]!];
  }
  const snap = snapToLine(start, line);
  if (snap && snap.alongM > lineDistanceM(line) / 2) return line.slice().reverse();
  return line;
}

export interface RouteTraceState {
  title: string | null | undefined;
  has_route: boolean;
  route_settings_done: boolean;
}

export interface RouteTraceProgress {
  route: boolean;
  settings: boolean;
  title: boolean;
}

export function routeTraceProgress(q: RouteTraceState): RouteTraceProgress {
  return {
    route: q.has_route,
    settings: q.has_route && q.route_settings_done,
    title: Boolean(q.title?.trim()),
  };
}

export function routeTraceReady(q: RouteTraceState): boolean {
  const p = routeTraceProgress(q);
  return p.route && p.settings && p.title;
}

export interface SpotTraceCheckpoint {
  title?: string | null;
  label?: string | null;
  thumbnail_image?: string | null;
  lat: number;
  lng: number;
}

/** Draft spots use (0,0) until the creator confirms a location in the picker. */
export function spotTraceHasLocation(s: Pick<SpotTraceCheckpoint, "lat" | "lng">): boolean {
  return Number.isFinite(s.lat) && Number.isFinite(s.lng) && !(s.lat === 0 && s.lng === 0);
}

export function spotTraceCheckpointReady(s: SpotTraceCheckpoint): boolean {
  return (
    Boolean(s.title?.trim()) &&
    Boolean(s.label?.trim()) &&
    Boolean(s.thumbnail_image?.trim()) &&
    spotTraceHasLocation(s)
  );
}

export interface SpotTraceState {
  title: string | null | undefined;
  checkpoints: SpotTraceCheckpoint[];
}

export function spotTraceReady(q: SpotTraceState): boolean {
  return Boolean(q.title?.trim()) && q.checkpoints.length >= 1 && q.checkpoints.every(spotTraceCheckpointReady);
}

export type SettingsStep = "startFinish" | "place" | "timeLimit";

/**
 * Spots the quest page and map show. With start anywhere on the route, the Start/Finish
 * spots the server keeps on the line's ends are bookkeeping only and never shown.
 */
export function isRouteEndpointCheckpoint(kind: string | null | undefined): boolean {
  return kind === "start" || kind === "finish";
}

/** Mission spots on a GPS Art quest (never Start/Finish endpoint rows). */
export function shownSpots<S extends { kind?: string | null }>(
  q: { start_fixed?: boolean; quest_type?: string | null; checkpoints: S[] },
): S[] {
  if (q.quest_type === "spot_trace") return q.checkpoints;
  return q.checkpoints.filter((s) => !isRouteEndpointCheckpoint(s.kind));
}

/** Route settings ask one thing per step; placing pins only applies to a fixed start. */
export function settingsSteps(startFixed: boolean): SettingsStep[] {
  return startFixed ? ["startFinish", "place", "timeLimit"] : ["startFinish", "timeLimit"];
}
